import React, {
  ChangeEvent, ReactNode, useCallback, useId,
} from 'react';

import Searcher from 'components/DesignSystem/Searcher';
import CreatorFilter, { CreatorSegment } from 'components/Flux/Personalized/MyStudies/FilterBar/CreatorFilter';
import FilterField from 'components/Flux/Personalized/MyStudies/FilterBar/FilterField';
import StatusFilter from 'components/Flux/Personalized/MyStudies/FilterBar/StatusFilter';
import { FluxStudyStatusCode } from 'constants/fluxStudyStatus';
import {
  MyStudiesFilters,
  MyStudiesFiltersChange,
} from 'types/Flux/Personalized/myStudiesFilters';

import './index.scss';

/** Placeholder da busca (.dc.html:137). */
export const MY_STUDIES_SEARCH_PLACEHOLDER = 'Buscar, ex.: ESTUDO0510GEFS35';

/**
 * Opções de uma mudança de filtros. Tipo estrutural: bate com o `SetFiltersOptions` que a #1997
 * acrescenta ao `useMyStudiesFilters` (`immediate` aplica sem esperar o debounce da busca).
 */
export interface FiltersChangeOptions {
  immediate?: boolean;
}

export interface FilterBarProps {
  /** Filtros aplicados (`useMyStudiesFilters().filters`). */
  filters: MyStudiesFilters;
  /** Texto do campo de busca como digitado (`useMyStudiesFilters().searchDraft`). */
  searchDraft: string;
  /**
   * `useMyStudiesFilters().setFilters`: replace na URL, página 1 e debounce da busca. O mesmo nome
   * do `FilterSummary` e do `MigrationNotice` (#1997). O `setFilters` de um argumento continua
   * atribuível; antes da #1997, a opção `immediate` é ignorada sem efeito.
   */
  onFiltersChange: (change: MyStudiesFiltersChange, options?: FiltersChangeOptions) => void;
  /** Papel de quem vê a tela (rótulo "Todos" do Criado por). */
  isAdmin: boolean;
  /**
   * `id` do campo de busca, dado pela tela para levar o foco até ele (ex.: ao dispensar o aviso de
   * "Casos Ampere", #1999). Sem ele, a barra gera o próprio.
   */
  searchInputId?: string;
  /** Campo Empresa (#2000), no fim da 1ª linha. O container só preenche para admin. */
  companySlot?: ReactNode;
  /** Campo Período · referente à (#1996), no começo da 2ª linha. */
  periodSlot?: ReactNode;
  /** Segmento "Usuário ▾" (#2000), dentro do grupo CRIADO POR, à direita dos segmentos. */
  userSlot?: ReactNode;
  /**
   * Rodapé do card: o resumo (`FilterSummary` da #1997, montado pela #1999), como no protótipo,
   * em que a contagem e os chips são o 3º filho do card (.dc.html:222). Entra como último filho
   * do card, sem wrapper, e fora da região de busca; o estilo de rodapé é do próprio resumo.
   */
  footer?: ReactNode;
}

/**
 * Barra de filtros da aba Meus Estudos (#1995): card branco com duas linhas de campos e um rodapé
 * (.dc.html:131-222).
 * - 1ª linha: NOME DO ESTUDO (Searcher do DS), STATUS e o slot da Empresa.
 * - 2ª linha: o slot do Período e CRIADO POR.
 * - Rodapé: `footer`. A #1999 passa o `<FilterSummary …/>` por ele, e a zona `summary` da casca
 *   fica vazia ou sai.
 *
 * Controlada só por props: não chama o `useMyStudiesFilters`. A tela tem uma única instância do
 * hook (a casca `MyStudies`), e uma segunda teria o próprio timer de busca. Cada mudança vai por
 * `onFiltersChange`, que grava na URL com replace e volta para a página 1. A busca espera 450 ms;
 * limpar (X do Searcher ou apagar tudo) pede `immediate`.
 *
 * Só as duas linhas de campos formam a região de busca (`role="search"`): a contagem e os chips
 * do rodapé ficam fora do landmark. O card não usa `overflow: hidden`, que cortaria o menu de
 * status.
 */
export default function FilterBar({
  filters,
  searchDraft,
  onFiltersChange,
  isAdmin,
  searchInputId,
  companySlot,
  periodSlot,
  userSlot,
  footer,
}: FilterBarProps) {
  const generatedSearchId = useId();
  const searchId = searchInputId ?? generatedSearchId;

  // `currentTarget`: o X do Searcher manda um evento fabricado com `target` e `currentTarget`.
  // Limpar (X, apagar tudo ou só espaços) não espera o debounce; texto espera os 450 ms.
  const handleSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    onFiltersChange({ busca: value }, { immediate: value.trim() === '' });
  }, [onFiltersChange]);

  const handleStatus = useCallback((status: FluxStudyStatusCode[]) => {
    onFiltersChange({ status });
  }, [onFiltersChange]);

  const handleCreator = useCallback((criadoPor: CreatorSegment) => {
    onFiltersChange({ criadoPor });
  }, [onFiltersChange]);

  return (
    <div className="my-studies-filter-bar">
      <div className="my-studies-filter-fields" role="search" aria-label="Filtros dos estudos">
        <div className="my-studies-filter-row my-studies-filter-row--primary">
          <FilterField label="NOME DO ESTUDO" htmlFor={searchId} className="my-studies-filter-search">
            <Searcher
              id={searchId}
              value={searchDraft}
              onChange={handleSearch}
              placeholder={MY_STUDIES_SEARCH_PLACEHOLDER}
            />
          </FilterField>
          <StatusFilter value={filters.status} onChange={handleStatus} />
          {companySlot}
        </div>
        <div className="my-studies-filter-row my-studies-filter-row--secondary">
          {periodSlot}
          <CreatorFilter
            value={filters.criadoPor}
            isAdmin={isAdmin}
            empresa={filters.empresa}
            onChange={handleCreator}
            userSlot={userSlot}
          />
        </div>
      </div>
      {footer}
    </div>
  );
}
